import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
    Upload,
    Download,
    Trash2,
    Loader2,
    FileText,
    FileImage,
    FileArchive,
    Film,
    File as FileIcon,
    Lock,
    ArrowLeft,
    Plus,
    FolderPlus,
    Folder,
    FolderOpen,
    Image as ImageIcon,
    ChevronRight,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import FocusHeader from "@/components/FocusHeader";
import OwnerAvatar from "@/components/OwnerAvatar";
import { TID } from "@/lib/testIds";
import { useRealtimeChannel } from "@/context/RealtimeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Production Documents — 3-level hierarchical file storage.
 *   /production/docs                       → Platforms grid (photo tiles)
 *   /production/docs/:platformId           → 8 fixed subsystem tiles
 *   /production/docs/:platformId/:sub      → Folder + file browser (nested folders)
 *
 * Clearance:
 *   • docs           → view / upload / create folders / create platforms
 *   • docs_download  → download bytes
 *   • admin          → delete anything
 */
export default function ProductionDocs() {
    const params = useParams();
    const [perms, setPerms] = useState({});

    useEffect(() => {
        api.get("/docs/permissions")
            .then(({ data }) => setPerms(data))
            .catch(() => setPerms({}));
    }, []);

    if (params.platformId && params.subsystem) {
        return <FolderBrowserView perms={perms} />;
    }
    if (params.platformId) {
        return <SubsystemsView perms={perms} />;
    }
    return <PlatformsView perms={perms} />;
}

// =============================================================================
// Level 1 — Platforms grid
// =============================================================================
function PlatformsView({ perms }) {
    const nav = useNavigate();
    const [platforms, setPlatforms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/docs/platforms");
            setPlatforms(data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load platforms.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useRealtimeChannel("docs", load);

    const remove = async (p) => {
        if (!perms.can_delete) return;
        if (!window.confirm(`Delete platform "${p.name}" and every file under it?`)) return;
        try {
            await api.delete(`/docs/platforms/${p.id}`);
            setPlatforms((prev) => prev.filter((x) => x.id !== p.id));
            toast.success("Platform deleted.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed.");
        }
    };

    return (
        <div
            className="min-h-screen md:h-screen bg-virtus-bg text-white flex flex-col md:overflow-hidden"
            data-testid={TID.prodDocsPage}
        >
            <FocusHeader
                code="AO/03·DOCS"
                title="Production Documents"
                subtitle="Platforms · pick a hull to open its subsystem files."
                backTo="/production"
            />

            <div className="flex flex-col md:flex-row md:items-center md:justify-between px-3 md:px-6 py-2 border-b border-virtus-line flex-shrink-0 gap-2 md:gap-0">
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim">
                    PLATFORMS //{" "}
                    <span className="text-white">{platforms.length}</span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => nav("/production")}
                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim border border-virtus-line px-3 py-1.5 hover:border-virtus-orange hover:text-virtus-orange"
                    >
                        <ArrowLeft size={11} /> BACK TO PRODUCTION
                    </button>
                    {perms.can_create_platform && (
                        <button
                            onClick={() => setShowAdd(true)}
                            className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-3 py-1.5 hover:bg-virtus-orange hover:text-black"
                            data-testid={TID.prodDocsAddPlatformBtn}
                        >
                            <Plus size={11} /> ADD PLATFORM
                        </button>
                    )}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6" data-kiosk-scroll="true">
                {loading ? (
                    <div className="py-16 flex items-center justify-center text-virtus-dim">
                        <Loader2 className="animate-spin" />
                    </div>
                ) : platforms.length === 0 ? (
                    <EmptyPlatforms onAdd={() => setShowAdd(true)} canAdd={!!perms.can_create_platform} />
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5">
                        <AnimatePresence>
                            {platforms.map((p) => (
                                <PlatformTile
                                    key={p.id}
                                    platform={p}
                                    canDelete={!!perms.can_delete}
                                    onOpen={() => nav(`/production/docs/${p.id}`)}
                                    onDelete={() => remove(p)}
                                />
                            ))}
                        </AnimatePresence>
                    </div>
                )}
            </div>

            {showAdd && (
                <AddPlatformModal
                    onClose={() => setShowAdd(false)}
                    onCreated={(p) => {
                        setPlatforms((prev) => [...prev, p]);
                        setShowAdd(false);
                    }}
                />
            )}
        </div>
    );
}

function PlatformTile({ platform, canDelete, onOpen, onDelete }) {
    // Cookie-auth: browser attaches access_token to same-origin <img src=…>.
    const imgUrl = platform.image_storage_path
        ? `${BACKEND_URL}/api/docs/platforms/${platform.id}/image`
        : null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="group relative aspect-[4/3] cursor-pointer border-2 border-virtus-line overflow-hidden bg-[#0A0A0A] hover:border-virtus-orange transition-all duration-200 hover:shadow-[0_0_28px_-4px_rgba(255,85,0,0.55)]"
            onClick={onOpen}
            data-testid={TID.prodDocsPlatformTile(platform.id)}
        >
            {imgUrl ? (
                <img
                    src={imgUrl}
                    alt={platform.name}
                    className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-300"
                    loading="lazy"
                />
            ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#141414] to-[#0A0A0A]">
                    <ImageIcon size={64} className="text-virtus-line" />
                </div>
            )}

            {/* Dark scrim so text reads over any photo */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent pointer-events-none" />

            {/* Corner brackets — only visible on hover */}
            <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-virtus-orange opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-virtus-orange opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-virtus-orange opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-virtus-orange opacity-0 group-hover:opacity-100 transition-opacity" />

            {/* Name */}
            <div className="absolute inset-x-0 bottom-0 px-4 py-3">
                <div
                    className="font-heading font-black uppercase tracking-tighter text-virtus-orange leading-none"
                    style={{ fontSize: "clamp(24px, 2.6vw, 42px)" }}
                >
                    {platform.name}
                </div>
                <div className="mt-1 font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-dim">
                    {platform.file_count || 0} FILE
                    {platform.file_count === 1 ? "" : "S"}
                </div>
            </div>

            {canDelete && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                    className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-black/70 border border-virtus-line text-virtus-faint opacity-0 group-hover:opacity-100 hover:text-virtus-stuck hover:border-virtus-stuck"
                    aria-label="Delete platform"
                    data-testid={TID.prodDocsPlatformDeleteBtn(platform.id)}
                >
                    <Trash2 size={13} />
                </button>
            )}
        </motion.div>
    );
}

function EmptyPlatforms({ onAdd, canAdd }) {
    return (
        <div className="py-16 flex flex-col items-center justify-center gap-4">
            <div className="w-16 h-16 border-2 border-virtus-line flex items-center justify-center">
                <ImageIcon size={28} className="text-virtus-faint" />
            </div>
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-faint">
                &gt; NO PLATFORMS ON FILE
            </div>
            {canAdd && (
                <button
                    onClick={onAdd}
                    className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-4 py-2 hover:bg-virtus-orange hover:text-black"
                >
                    <Plus size={11} /> ADD FIRST PLATFORM
                </button>
            )}
        </div>
    );
}

function AddPlatformModal({ onClose, onCreated }) {
    const [name, setName] = useState("");
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const fileInputRef = useRef(null);

    const onPick = (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (!f.type.startsWith("image/")) {
            toast.error("Pick an image file (jpg / png / webp / gif).");
            return;
        }
        if (f.size > 15 * 1024 * 1024) {
            toast.error("Image exceeds 15 MB.");
            return;
        }
        setFile(f);
        setPreview(URL.createObjectURL(f));
    };

    const submit = async () => {
        const trimmed = name.trim();
        if (!trimmed) return toast.warning("Name is required.");
        setSaving(true);
        try {
            const form = new FormData();
            form.append("name", trimmed);
            if (file) form.append("image", file);
            const { data } = await api.post("/docs/platforms", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Platform created.");
            onCreated(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Create failed.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 backdrop-blur-md">
            <div className="w-[520px] max-w-[92vw] bg-[#0A0A0A] border-2 border-virtus-orange">
                <div className="flex items-center justify-between px-5 py-3 border-b border-virtus-line">
                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange">
                        NEW PLATFORM
                    </div>
                    <button
                        onClick={onClose}
                        className="text-virtus-dim hover:text-virtus-orange"
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </div>
                <div className="p-5 space-y-4">
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim mb-1.5">
                            NAME
                        </div>
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. VM18"
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono uppercase tracking-wider"
                            autoFocus
                            data-testid={TID.prodDocsPlatformNameInput}
                        />
                    </div>
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim mb-1.5">
                            PHOTO (OPTIONAL · MAX 15 MB)
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={onPick}
                            className="hidden"
                            data-testid={TID.prodDocsPlatformImageInput}
                        />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="w-full aspect-[4/3] border-2 border-dashed border-virtus-line hover:border-virtus-orange flex items-center justify-center relative overflow-hidden group"
                        >
                            {preview ? (
                                <>
                                    <img
                                        src={preview}
                                        alt="preview"
                                        className="absolute inset-0 w-full h-full object-cover opacity-80"
                                    />
                                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange">
                                            REPLACE IMAGE
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center">
                                    <ImageIcon size={32} className="mx-auto text-virtus-faint mb-2" />
                                    <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-faint">
                                        CLICK TO PICK
                                    </div>
                                </div>
                            )}
                        </button>
                    </div>
                </div>
                <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-virtus-line">
                    <button
                        onClick={onClose}
                        className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim hover:text-white px-3 py-2"
                    >
                        CANCEL
                    </button>
                    <button
                        onClick={submit}
                        disabled={saving || !name.trim()}
                        className="font-mono text-[10px] tracking-[0.22em] uppercase bg-virtus-orange text-black px-4 py-2 hover:bg-white glow-orange disabled:opacity-50"
                        data-testid={TID.prodDocsPlatformCreateBtn}
                    >
                        {saving ? "SAVING…" : "CREATE PLATFORM"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// Level 2 — Subsystems grid (default 8 + custom)
// =============================================================================
function SubsystemsView({ perms }) {
    const { platformId } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/docs/platforms/${platformId}/subsystems`);
            setData(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load subsystems.");
            if (e.response?.status === 404) nav("/production/docs");
        } finally {
            setLoading(false);
        }
    }, [platformId, nav]);

    useEffect(() => {
        load();
    }, [load]);

    useRealtimeChannel("docs", load);

    const remove = async (s) => {
        if (!perms.can_delete) return;
        if (!window.confirm(`Delete subsystem "${s.label}" and every folder/file under it?`)) return;
        try {
            await api.delete(`/docs/platforms/${platformId}/subsystems/${s.key}`);
            toast.success("Subsystem deleted.");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Delete failed.");
        }
    };

    return (
        <div
            className="min-h-screen md:h-screen bg-virtus-bg text-white flex flex-col md:overflow-hidden"
            data-testid={TID.prodDocsSubsystemsPage}
        >
            <FocusHeader
                code="AO/03·DOCS"
                title={data?.platform?.name || "…"}
                subtitle="Pick a subsystem to open its files and folders."
                backTo="/production/docs"
            />

            <div className="flex flex-col md:flex-row md:items-center md:justify-between px-3 md:px-6 py-2 border-b border-virtus-line flex-shrink-0 gap-2 md:gap-0">
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim">
                    SUBSYSTEMS //{" "}
                    <span className="text-white">{(data?.subsystems || []).length}</span>{" "}
                    SECTIONS
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => nav("/production/docs")}
                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim border border-virtus-line px-3 py-1.5 hover:border-virtus-orange hover:text-virtus-orange"
                    >
                        <ArrowLeft size={11} /> ALL PLATFORMS
                    </button>
                    {perms.can_create_platform && (
                        <button
                            onClick={() => setShowAdd(true)}
                            className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-3 py-1.5 hover:bg-virtus-orange hover:text-black"
                            data-testid={TID.prodDocsAddSubsystemBtn}
                        >
                            <Plus size={11} /> ADD SUBSYSTEM
                        </button>
                    )}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6" data-kiosk-scroll="true">
                {loading ? (
                    <div className="py-16 flex items-center justify-center text-virtus-dim">
                        <Loader2 className="animate-spin" />
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {(data?.subsystems || []).map((s) => (
                            <SubsystemTile
                                key={s.key}
                                subsystem={s}
                                platformId={platformId}
                                canDelete={!!perms.can_delete && !s.is_default}
                                onOpen={() => nav(`/production/docs/${platformId}/${s.key}`)}
                                onDelete={() => remove(s)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {showAdd && (
                <AddSubsystemModal
                    platformId={platformId}
                    onClose={() => setShowAdd(false)}
                    onCreated={() => {
                        setShowAdd(false);
                        load();
                    }}
                />
            )}
        </div>
    );
}

function SubsystemTile({ subsystem, platformId, canDelete, onOpen, onDelete }) {
    const total = (subsystem.file_count || 0) + (subsystem.folder_count || 0);
    // Cookie-auth: browser attaches access_token to same-origin <img src=…>.
    const imgUrl = subsystem.image_available
        ? `${BACKEND_URL}/api/docs/platforms/${platformId}/subsystems/${subsystem.key}/image`
        : null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="group relative aspect-[16/10] cursor-pointer border-2 border-virtus-line bg-[#0A0A0A] hover:border-virtus-orange hover:shadow-[0_0_28px_-4px_rgba(255,85,0,0.55)] transition-all duration-200 overflow-hidden"
            onClick={onOpen}
            data-testid={TID.prodDocsSubsystemTile(subsystem.key)}
        >
            {imgUrl && (
                <>
                    <img
                        src={imgUrl}
                        alt={subsystem.label}
                        className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-300"
                        loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent pointer-events-none" />
                </>
            )}

            {/* Corner brackets on hover */}
            <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-virtus-orange opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-virtus-orange opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-virtus-orange opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-virtus-orange opacity-0 group-hover:opacity-100 transition-opacity" />

            <div className="absolute inset-0 flex flex-col justify-between p-6">
                <div className="flex items-start justify-between">
                    <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-virtus-dim group-hover:text-virtus-orange transition-colors">
                        {subsystem.key.replace(/_/g, " · ").toUpperCase()}
                    </div>
                    {!subsystem.is_default && (
                        <span
                            className="font-mono text-[9px] tracking-[0.22em] uppercase text-virtus-orange bg-virtus-orange/10 border border-virtus-orange/40 px-1.5 py-0.5"
                            title="Custom subsystem"
                        >
                            CUSTOM
                        </span>
                    )}
                </div>
                <div>
                    <div
                        className="font-heading font-black uppercase tracking-tighter leading-none text-white"
                        style={{ fontSize: "clamp(24px, 2.6vw, 40px)" }}
                    >
                        {subsystem.label}
                    </div>
                    <div className="mt-2 font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-dim">
                        <span className="text-virtus-orange">{subsystem.folder_count}</span> FOLDER
                        {subsystem.folder_count === 1 ? "" : "S"} ·{" "}
                        <span className="text-virtus-orange">{subsystem.file_count}</span> FILE
                        {subsystem.file_count === 1 ? "" : "S"}
                        {total === 0 && (
                            <span className="ml-2 text-virtus-faint">// EMPTY</span>
                        )}
                    </div>
                </div>
            </div>

            {canDelete && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                    className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-black/70 border border-virtus-line text-virtus-faint opacity-0 group-hover:opacity-100 hover:text-virtus-stuck hover:border-virtus-stuck"
                    aria-label="Delete subsystem"
                    data-testid={TID.prodDocsSubsystemDeleteBtn(subsystem.key)}
                >
                    <Trash2 size={13} />
                </button>
            )}
        </motion.div>
    );
}

function AddSubsystemModal({ platformId, onClose, onCreated }) {
    const [label, setLabel] = useState("");
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const fileInputRef = useRef(null);

    const onPick = (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (!f.type.startsWith("image/")) {
            toast.error("Pick an image file (jpg / png / webp / gif).");
            return;
        }
        if (f.size > 15 * 1024 * 1024) {
            toast.error("Image exceeds 15 MB.");
            return;
        }
        setFile(f);
        setPreview(URL.createObjectURL(f));
    };

    const submit = async () => {
        const trimmed = label.trim();
        if (!trimmed) return toast.warning("Subsystem name is required.");
        setSaving(true);
        try {
            const form = new FormData();
            form.append("label", trimmed);
            if (file) form.append("image", file);
            await api.post(`/docs/platforms/${platformId}/subsystems`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Subsystem created.");
            onCreated();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Create failed.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 backdrop-blur-md">
            <div className="w-[520px] max-w-[92vw] bg-[#0A0A0A] border-2 border-virtus-orange">
                <div className="flex items-center justify-between px-5 py-3 border-b border-virtus-line">
                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange">
                        NEW SUBSYSTEM
                    </div>
                    <button
                        onClick={onClose}
                        className="text-virtus-dim hover:text-virtus-orange"
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </div>
                <div className="p-5 space-y-4">
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim mb-1.5">
                            NAME
                        </div>
                        <input
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && submit()}
                            placeholder="e.g. Manufacturing, Testing, AI Modeling"
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                            autoFocus
                            data-testid={TID.prodDocsSubsystemNameInput}
                        />
                    </div>
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim mb-1.5">
                            PHOTO (OPTIONAL · MAX 15 MB)
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={onPick}
                            className="hidden"
                            data-testid={TID.prodDocsSubsystemImageInput}
                        />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="w-full aspect-[16/10] border-2 border-dashed border-virtus-line hover:border-virtus-orange flex items-center justify-center relative overflow-hidden group"
                        >
                            {preview ? (
                                <>
                                    <img
                                        src={preview}
                                        alt="preview"
                                        className="absolute inset-0 w-full h-full object-cover opacity-80"
                                    />
                                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange">
                                            REPLACE IMAGE
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center">
                                    <ImageIcon size={32} className="mx-auto text-virtus-faint mb-2" />
                                    <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-faint">
                                        CLICK TO PICK
                                    </div>
                                </div>
                            )}
                        </button>
                    </div>
                </div>
                <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-virtus-line">
                    <button
                        onClick={onClose}
                        className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim hover:text-white px-3 py-2"
                    >
                        CANCEL
                    </button>
                    <button
                        onClick={submit}
                        disabled={saving || !label.trim()}
                        className="font-mono text-[10px] tracking-[0.22em] uppercase bg-virtus-orange text-black px-4 py-2 hover:bg-white glow-orange disabled:opacity-50"
                        data-testid={TID.prodDocsSubsystemCreateBtn}
                    >
                        {saving ? "SAVING…" : "CREATE SUBSYSTEM"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// Level 3 — Folder + file browser (nested folders)
// =============================================================================
function FolderBrowserView({ perms }) {
    const { platformId, subsystem } = useParams();
    const nav = useNavigate();
    const [folderId, setFolderId] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [showAddFolder, setShowAddFolder] = useState(false);
    const fileInputRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const q = new URLSearchParams({ platform_id: platformId, subsystem });
            if (folderId) q.append("folder_id", folderId);
            const { data } = await api.get(`/docs/browse?${q.toString()}`);
            setData(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load.");
            if (e.response?.status === 404) {
                if (folderId) setFolderId(null);
                else nav(`/production/docs/${platformId}`);
            }
        } finally {
            setLoading(false);
        }
    }, [platformId, subsystem, folderId, nav]);

    useEffect(() => {
        setLoading(true);
        load();
    }, [load]);

    useRealtimeChannel("docs", load);

    const onFileChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > 50 * 1024 * 1024) {
            toast.error("File exceeds 50 MB limit.");
            return;
        }
        setUploading(true);
        try {
            const form = new FormData();
            form.append("platform_id", platformId);
            form.append("subsystem", subsystem);
            if (folderId) form.append("folder_id", folderId);
            form.append("file", file);
            await api.post("/docs/files", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            await load();
            toast.success(`Uploaded: ${file.name}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed.");
        } finally {
            setUploading(false);
        }
    };

    const createFolder = async (name) => {
        try {
            await api.post("/docs/folders", {
                platform_id: platformId,
                subsystem,
                parent_folder_id: folderId,
                name,
            });
            await load();
            setShowAddFolder(false);
            toast.success("Folder created.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Create failed.");
        }
    };

    const download = (f) => {
        if (!perms.can_download) {
            toast.warning("Download requires 'docs_download' clearance.");
            return;
        }
        // Cookie-auth: browser attaches access_token to same-origin <a href>.
        const url = `${BACKEND_URL}/api/docs/files/${f.id}/download`;
        const a = document.createElement("a");
        a.href = url;
        a.rel = "noopener";
        a.click();
    };

    const removeFile = async (f) => {
        if (!perms.can_delete) return;
        if (!window.confirm(`Delete "${f.filename}"?`)) return;
        try {
            await api.delete(`/docs/files/${f.id}`);
            await load();
            toast.success("Deleted.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Delete failed.");
        }
    };

    const removeFolder = async (folder) => {
        if (!perms.can_delete) return;
        if (!window.confirm(`Delete folder "${folder.name}" and everything inside?`)) return;
        try {
            await api.delete(`/docs/folders/${folder.id}`);
            await load();
            toast.success("Folder deleted.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Delete failed.");
        }
    };

    const breadcrumb = useMemo(() => data?.breadcrumb || [], [data]);
    const folders = data?.folders || [];
    const files = data?.files || [];

    return (
        <div
            className="min-h-screen md:h-screen bg-virtus-bg text-white flex flex-col md:overflow-hidden"
            data-testid={TID.prodDocsBrowsePage}
        >
            <FocusHeader
                code="AO/03·DOCS"
                title={data ? `${data.platform.name} · ${data.subsystem.label}` : "…"}
                subtitle="Files and folders. Add new folders (tiles) or upload documents."
                backTo={`/production/docs/${platformId}`}
            />

            <div className="flex flex-col md:flex-row md:items-center md:justify-between px-3 md:px-6 py-2 border-b border-virtus-line flex-shrink-0 gap-2 md:gap-4">
                <div className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-dim min-w-0 overflow-hidden">
                    <button
                        onClick={() => setFolderId(null)}
                        className={`hover:text-virtus-orange ${folderId ? "" : "text-virtus-orange"}`}
                    >
                        ROOT
                    </button>
                    {breadcrumb.map((b, i) => (
                        <span key={b.id} className="flex items-center gap-1.5 min-w-0">
                            <ChevronRight size={10} className="text-virtus-faint flex-shrink-0" />
                            <button
                                onClick={() => setFolderId(b.id)}
                                className={`truncate ${
                                    i === breadcrumb.length - 1
                                        ? "text-virtus-orange"
                                        : "hover:text-virtus-orange"
                                }`}
                                title={b.name}
                            >
                                {b.name.toUpperCase()}
                            </button>
                        </span>
                    ))}
                    {!perms.can_download && (
                        <span className="ml-3 text-virtus-stuck flex-shrink-0">
                            <Lock size={10} className="inline -mt-0.5 mr-1" />
                            DL LOCKED
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {perms.can_create_folder && (
                        <button
                            onClick={() => setShowAddFolder(true)}
                            className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-3 py-1.5 hover:bg-virtus-orange hover:text-black"
                            data-testid={TID.prodDocsAddFolderBtn}
                        >
                            <FolderPlus size={11} /> NEW FOLDER
                        </button>
                    )}
                    {perms.can_upload && (
                        <>
                            <input
                                ref={fileInputRef}
                                type="file"
                                onChange={onFileChange}
                                className="hidden"
                                data-testid={TID.prodDocsFileInput}
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-3 py-1.5 hover:bg-virtus-orange hover:text-black disabled:opacity-50"
                                data-testid={TID.prodDocsUploadBtn}
                            >
                                {uploading ? (
                                    <Loader2 size={11} className="animate-spin" />
                                ) : (
                                    <Upload size={11} />
                                )}
                                {uploading ? "UPLOADING…" : "UPLOAD FILE"}
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6" data-kiosk-scroll="true">
                {loading ? (
                    <div className="py-16 flex items-center justify-center text-virtus-dim">
                        <Loader2 className="animate-spin" />
                    </div>
                ) : folders.length === 0 && files.length === 0 ? (
                    <EmptyBrowse
                        onAddFolder={() => setShowAddFolder(true)}
                        onUpload={() => fileInputRef.current?.click()}
                        canAddFolder={!!perms.can_create_folder}
                        canUpload={!!perms.can_upload}
                    />
                ) : (
                    <>
                        {folders.length > 0 && (
                            <div>
                                <div className="font-mono text-[9px] tracking-[0.32em] uppercase text-virtus-faint mb-3">
                                    FOLDERS · {folders.length}
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
                                    {folders.map((f) => (
                                        <FolderTile
                                            key={f.id}
                                            folder={f}
                                            canDelete={!!perms.can_delete}
                                            onOpen={() => setFolderId(f.id)}
                                            onDelete={() => removeFolder(f)}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                        {files.length > 0 && (
                            <div>
                                <div className="font-mono text-[9px] tracking-[0.32em] uppercase text-virtus-faint mb-3">
                                    FILES · {files.length}
                                </div>
                                <div className="border border-virtus-line">
                                    <div className="grid grid-cols-[40px_1fr_120px_180px_140px_120px] items-center gap-2 px-4 h-[26px] border-b border-virtus-line font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-faint bg-virtus-bg">
                                        <div />
                                        <div>FILE</div>
                                        <div>SIZE</div>
                                        <div>UPLOADED BY</div>
                                        <div>UPLOADED</div>
                                        <div className="text-right pr-3">ACTIONS</div>
                                    </div>
                                    {files.map((f) => (
                                        <FileRow
                                            key={f.id}
                                            doc={f}
                                            perms={perms}
                                            onDownload={() => download(f)}
                                            onDelete={() => removeFile(f)}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {showAddFolder && (
                <AddFolderModal
                    onClose={() => setShowAddFolder(false)}
                    onCreate={createFolder}
                />
            )}
        </div>
    );
}

function FolderTile({ folder, canDelete, onOpen, onDelete }) {
    return (
        <button
            onClick={onOpen}
            className="group relative aspect-[16/9] border-2 border-virtus-line bg-[#0A0A0A] hover:border-virtus-orange hover:shadow-[0_0_28px_-4px_rgba(255,85,0,0.55)] transition-all duration-200 flex items-center gap-4 px-5 text-left"
            data-testid={TID.prodDocsFolderTile(folder.id)}
        >
            <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center border border-virtus-line group-hover:border-virtus-orange text-virtus-orange">
                <Folder size={22} className="group-hover:hidden" />
                <FolderOpen size={22} className="hidden group-hover:block" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-heading text-lg font-black uppercase tracking-tight text-white truncate">
                    {folder.name}
                </div>
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-dim">
                    OPEN
                </div>
            </div>
            {canDelete && (
                <span
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                    className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-black/70 border border-virtus-line text-virtus-faint opacity-0 group-hover:opacity-100 hover:text-virtus-stuck hover:border-virtus-stuck cursor-pointer"
                    role="button"
                    aria-label="Delete folder"
                    data-testid={TID.prodDocsFolderDeleteBtn(folder.id)}
                >
                    <Trash2 size={11} />
                </span>
            )}
        </button>
    );
}

function FileRow({ doc, perms, onDownload, onDelete }) {
    const Icon = iconForMime(doc.content_type, doc.filename);
    return (
        <div
            className="grid grid-cols-[40px_1fr_120px_180px_140px_120px] items-center gap-2 px-4 h-11 border-b border-virtus-line last:border-b-0 hover:bg-[#141414]"
            data-testid={TID.prodDocsRow(doc.id)}
        >
            <div className="flex items-center justify-center text-virtus-orange">
                <Icon size={16} />
            </div>
            <div className="text-sm text-white truncate" title={doc.filename}>
                {doc.filename}
            </div>
            <div className="font-mono text-xs text-virtus-dim tabular-nums">
                {formatBytes(doc.size)}
            </div>
            <div className="flex items-center gap-2 min-w-0">
                {doc.uploaded_by && (
                    <OwnerAvatar owner={doc.uploaded_by_name || doc.uploaded_by} size={20} />
                )}
                <span className="font-mono text-[11px] text-virtus-dim truncate">
                    {doc.uploaded_by_name || doc.uploaded_by || "—"}
                </span>
            </div>
            <div className="font-mono text-[11px] text-virtus-dim tabular-nums">
                {formatWhen(doc.created_at)}
            </div>
            <div className="flex items-center justify-end gap-2 pr-1">
                {perms.can_download ? (
                    <button
                        onClick={onDownload}
                        className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-orange border border-virtus-orange px-2 py-1 hover:bg-virtus-orange hover:text-black"
                        data-testid={TID.prodDocsDownloadBtn(doc.id)}
                    >
                        <Download size={11} /> GET
                    </button>
                ) : (
                    <span
                        className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-faint border border-virtus-line/50 px-2 py-1"
                        title="Requires 'docs_download' clearance"
                    >
                        <Lock size={11} /> LOCKED
                    </span>
                )}
                {perms.can_delete && (
                    <button
                        onClick={onDelete}
                        className="text-virtus-faint hover:text-virtus-stuck"
                        aria-label="Delete document"
                        data-testid={TID.prodDocsDeleteBtn(doc.id)}
                    >
                        <Trash2 size={13} />
                    </button>
                )}
            </div>
        </div>
    );
}

function EmptyBrowse({ onAddFolder, onUpload, canAddFolder, canUpload }) {
    return (
        <div className="py-16 flex flex-col items-center justify-center gap-4">
            <div className="w-16 h-16 border-2 border-virtus-line flex items-center justify-center">
                <Folder size={28} className="text-virtus-faint" />
            </div>
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-faint">
                &gt; NOTHING HERE YET
            </div>
            <div className="flex items-center gap-3">
                {canAddFolder && (
                    <button
                        onClick={onAddFolder}
                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-4 py-2 hover:bg-virtus-orange hover:text-black"
                    >
                        <FolderPlus size={11} /> NEW FOLDER
                    </button>
                )}
                {canUpload && (
                    <button
                        onClick={onUpload}
                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-4 py-2 hover:bg-virtus-orange hover:text-black"
                    >
                        <Upload size={11} /> UPLOAD FILE
                    </button>
                )}
            </div>
        </div>
    );
}

function AddFolderModal({ onClose, onCreate }) {
    const [name, setName] = useState("");
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        const trimmed = name.trim();
        if (!trimmed) return toast.warning("Name is required.");
        setSaving(true);
        try {
            await onCreate(trimmed);
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 backdrop-blur-md">
            <div className="w-[460px] max-w-[92vw] bg-[#0A0A0A] border-2 border-virtus-orange">
                <div className="flex items-center justify-between px-5 py-3 border-b border-virtus-line">
                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange">
                        NEW FOLDER
                    </div>
                    <button
                        onClick={onClose}
                        className="text-virtus-dim hover:text-virtus-orange"
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </div>
                <div className="p-5 space-y-3">
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim mb-1.5">
                            NAME
                        </div>
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && submit()}
                            placeholder="e.g. Hull Layup"
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                            autoFocus
                            data-testid={TID.prodDocsFolderNameInput}
                        />
                    </div>
                </div>
                <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-virtus-line">
                    <button
                        onClick={onClose}
                        className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim hover:text-white px-3 py-2"
                    >
                        CANCEL
                    </button>
                    <button
                        onClick={submit}
                        disabled={saving || !name.trim()}
                        className="font-mono text-[10px] tracking-[0.22em] uppercase bg-virtus-orange text-black px-4 py-2 hover:bg-white glow-orange disabled:opacity-50"
                        data-testid={TID.prodDocsFolderCreateBtn}
                    >
                        {saving ? "SAVING…" : "CREATE"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// Utilities
// =============================================================================
function iconForMime(mime, filename) {
    const m = (mime || "").toLowerCase();
    const ext = ((filename || "").split(".").pop() || "").toLowerCase();
    if (m.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp"].includes(ext))
        return FileImage;
    if (m.startsWith("video/") || ["mp4", "mov", "webm"].includes(ext)) return Film;
    if (m === "application/pdf" || ext === "pdf") return FileText;
    if (["zip", "tar", "gz", "7z", "rar"].includes(ext)) return FileArchive;
    if (m.startsWith("text/") || ["md", "txt", "csv", "json", "xml"].includes(ext))
        return FileText;
    return FileIcon;
}

function formatBytes(n) {
    if (!n || n <= 0) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    let v = n;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i += 1;
    }
    return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatWhen(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const date = d
            .toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "2-digit", timeZone: "UTC" })
            .toUpperCase()
            .replace(/,/g, "");
        const t = d.toLocaleTimeString("en-US", {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
            timeZone: "UTC",
        });
        return `${date} · ${t}Z`;
    } catch (_) {
        return iso.slice(0, 16);
    }
}
